import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { getDefaultDateRange } from '../../../../lib/utils';
import { useProject } from '../../../../context/ProjectContext';
import { useAccess } from '../../../../hooks/useAccess';
import Loader from '../../../elements/Loader';
import DashboardFilters from './metrics/DashboardFilters';
import ExportButtonAndFilters from './ExportButtonAndFilters';
import DashboardSections from './DashboardSections';

const DashboardTabContent = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const { canAccess } = useAccess();

  const {
    isReady: isProjectReady,
    currentProject,
    isLoading: isProjectLoading,
  } = useProject();

  const sectionRefs = useRef({});
  const pageRef = useRef(null);
  const isAdmin = canAccess('ViewProjectDashboard');

  const [exporting, setExporting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isJiraIntegrated, setIsJiraIntegrated] = useState(false);
  const [linkedJiraProjects, setLinkedJiraProjects] = useState([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState(null);

  const [dateFilters, setDateFilters] = useState({
    ...getDefaultDateRange(),
    defaultRange: true,
  });

  const currentView = useMemo(
    () => new URLSearchParams(location.search).get('view'),
    [location.search]
  );

  useEffect(() => {
    if (!currentProject) return;

    const jiraProjects = currentProject.linked_jira_projects || [];

    setLinkedJiraProjects(jiraProjects);
    setSelectedJiraProject((prev) => {
      if (prev && jiraProjects.some((p) => p.id === prev.id)) {
        return prev;
      }

      return jiraProjects[0] || null;
    });
    setIsJiraIntegrated(
      currentProject.integrated_platform === 'jira' &&
      jiraProjects.length > 0
    );
  }, [currentProject]);

  const handleApplyFilters = (filters) => {
    setDateFilters(filters);
  };

  const handleJiraProjectChange = (project) => {
    setSelectedJiraProject(project);
  };

  if (!isProjectReady || isProjectLoading) {
    return <Loader />;
  }

  return (
    <Container fluid className="py-2 px-0">
      <div className={currentView ? 'sticky-top-bar' : ''}>
        <ExportButtonAndFilters
          exporting={exporting}
          setExporting={setExporting}
          currentProject={currentProject}
          selectedJiraProject={selectedJiraProject}
          linkedJiraProjects={linkedJiraProjects}
          isJiraIntegrated={isJiraIntegrated}
          currentView={currentView}
          setIsFilterOpen={setIsFilterOpen}
          handleJiraProjectChange={handleJiraProjectChange}
          pageRef={pageRef}
          sectionRefs={sectionRefs}
        />

        <DashboardFilters
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          dateFilters={dateFilters}
          linkedJiraProjects={linkedJiraProjects}
        />
      </div>

      <div ref={pageRef} className="dashboard-report-section">
        <DashboardSections
          currentView={currentView}
          projectId={projectId}
          isAdmin={isAdmin}
          dateFilters={dateFilters}
          isJiraIntegrated={isJiraIntegrated}
          selectedJiraProject={selectedJiraProject}
          sectionRefs={sectionRefs}
        />
      </div>
    </Container>
  );
};

export default DashboardTabContent;
